import type { TFunction } from 'i18next'
import { FilePenLine, Trash2 } from 'lucide-react'

import { TableColumn } from '@/components/Table'
import { FreezePosition } from '@/components/Table/tableHelper'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { ResponseUserMapping } from '@/schema'
import { formatFullName } from '@/utils/common'

export type UserMappingAction = 'edit' | 'delete'

export const getUserMappingListColumns = (
  handleActionClick: (
    action: UserMappingAction,
    item: ResponseUserMapping
  ) => void,
  t: TFunction
): TableColumn<ResponseUserMapping>[] => [
  {
    id: 'vista_username',
    label: t('userMapping.columns.vistaUser'),
    width: 300,
    sortable: true,
    renderCell: (_, row) => {
      const stringPaths = []
      if (row.vista_first_name && row.vista_last_name) {
        stringPaths.push(
          formatFullName(row.vista_first_name, row.vista_last_name)
        )
      }
      if (row.vista_user_email) stringPaths.push(row.vista_user_email)

      return (
        <div className="flex flex-col font-medium">
          {stringPaths.join(' / ')}
        </div>
      )
    },
  },
  {
    id: 'teamwork_username',
    label: t('userMapping.columns.teamworkUser'),
    width: 300,
    sortable: true,
    renderCell: (value, row) => {
      const stringPaths = []
      if (value) stringPaths.push(String(value))

      if (row.teamwork_user_email && row.has_mapping) {
        stringPaths.push(row.teamwork_user_email)
      }

      return (
        <div className="flex flex-col font-medium">
          {stringPaths.length > 0 ? stringPaths.join(' / ') : '-'}
        </div>
      )
    },
  },
  {
    id: 'actions',
    width: 90,
    isFrozen: true,
    freezePosition: FreezePosition.RIGHT,
    renderCell: (_value, row) => (
      <div className="flex gap-0.5 items-center">
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleActionClick('edit', row)
              }}
            >
              <FilePenLine size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{t('userMapping.actions.edit')}</span>
          </TooltipContent>
        </Tooltip>
        <Tooltip delayDuration={0}>
          <TooltipTrigger asChild>
            <Button
              variant="text"
              className="border-none bg-transparent p-1.5 w-fit h-fit text-foreground-fg-quaternary-400 hover:bg-transparent"
              onClick={(e) => {
                e.stopPropagation()
                handleActionClick('delete', row)
              }}
            >
              <Trash2 size={16} />
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <span>{t('userMapping.actions.delete')}</span>
          </TooltipContent>
        </Tooltip>
      </div>
    ),
  },
]
